import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DeleteDocumentMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

const mapTrpcResponseToGraphQL = (): DeleteDocumentMutation => {
  return {
    deleteDocumentById: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  };
};

export const useDeleteDocumentTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.document.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.document.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.document.documentById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.document.list.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification, trpcEnabled]);

  return {
    deleteDocument: async (id: string): Promise<DeleteDocumentMutation> => {
      await mutation.mutateAsync({ id });

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
